import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  PageContainer,
  MaxWidthContainer,
  TopBar,
  StickyTopBar,
  MainGrid,
  SideNavContainer,
  MainContent,
  FooterNote,
  AvatarContainer,
  SecurityConsole,
  SecurityConsoleHeader,
  SecurityConsoleContent,
  NavButton,
  NavButtonLeft,
  NavButtonIcon,
  NavButtonLabel,
  NavButtonRight,
  ContentGrid,
  Grid2,
  Grid3,
  Grid4,
} from "./styles";
import {
  Icon,
  Card,
  Button,
  Field,
  Toggle,
  Modal,
  Pill,
  ProgressRing,
  Otp6,
  ToastStack,
} from "./components";
import { useChainContext } from "../../../context/context";
import {
  getUserInfo,
  uploadProfile,
  create,
  tfaEnable,
  tfaDisable,
  verifyTFA,
  changePassword,
  sendOtpDynamic,
  SendTwoFactor,
  editAddressInfo,
  getAddressInfo,
} from "../../../../utils/services/user.service";
import ProfileView from "./views/ProfileView";
import SecurityView from "./views/SecurityView";
import SessionsView from "./views/SessionsView";
import AuditView from "./views/AuditView";
import ComplianceView from "./views/ComplianceView";
import AddressesView from "./views/AddressesView";
import KYBView from "./views/KYBView";
import KYCView from "./views/KYCView";
import OtherView from "./views/OtherView";

const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
const uid = () => Math.random().toString(16).slice(2) + Date.now().toString(16);

const riskLabel = (v) => {
  if (v >= 75) return { t: "High", tone: "b" };
  if (v >= 45) return { t: "Med", tone: "c" };
  return { t: "Low", tone: "d" };
};

const makeBackupCodes = () => {
  const rnd = () => Math.random().toString(16).slice(2, 6).toUpperCase();
  return Array.from({ length: 10 }, () => rnd() + "-" + rnd());
};

const AccountManagement = () => {
  const { setRefreshUserData } = useChainContext();
  const [tab, setTab] = useState("profile");
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);

  // Get initial user data from localStorage
  const getInitialUserData = () => {
    try {
      const storedUser = JSON.parse(localStorage.getItem("user"));
      return storedUser?.data?.user || null;
    } catch (error) {
      console.error("Error parsing user data from localStorage:", error);
      return null;
    }
  };

  // Initialize state from localStorage
  const initialUser = getInitialUserData();
  const getInitialName = () => {
    if (initialUser?.firstName && initialUser?.lastName) {
      return `${initialUser.firstName} ${initialUser.lastName}`;
    }
    if (initialUser?.firstName) return initialUser.firstName;
    if (initialUser?.lastName) return initialUser.lastName;
    return "";
  };

  // Profile state
  const [fullName, setFullName] = useState(getInitialName());
  const initials = useMemo(() => {
    const parts = (fullName || "").trim().split(/\s+/).filter(Boolean);
    const a = parts[0]?.[0] || "U";
    const b = parts.length > 1 ? parts[parts.length - 1]?.[0] || "S" : "S";
    return (a + b).toUpperCase();
  }, [fullName]);
  const [account, setAccount] = useState(initialUser?.username ? `@${initialUser.username}` : initialUser?.email?.split("@")[0] ? `@${initialUser.email.split("@")[0]}` : "@user");
  const [accountLocked, setAccountLocked] = useState(false);
  const [accountCheck, setAccountCheck] = useState("idle");
  const [accountLastChecked, setAccountLastChecked] = useState("@");
  const [email, setEmail] = useState(initialUser?.email || "");
  const [phone, setPhone] = useState(initialUser?.phone || initialUser?.phoneNumber || "");
  const [avatar, setAvatar] = useState(initialUser?.image || "");
  const [cover, setCover] = useState("");

  // Wallet addresses (map API fields: address → eth, secondary_wallet_sol_address → sol, secondary_wallet_address_tron → trc)
  const [wallets, setWallets] = useState({
    sol: initialUser?.sol_address || initialUser?.secondary_wallet_sol_address || "",
    btc: initialUser?.btcAddress || "",
    trc: initialUser?.trc_address || initialUser?.secondary_wallet_address_tron || "",
    eth: initialUser?.eth_address || "",
    bnb: initialUser?.initialUser?.address ||  "",
  });

  // Security state: otp_status = "Two Factor" (login OTP) via SendTwoFactor; google_auth_status = "2FA Authenticator"
  const [statusTwoFA, setStatusTwoFA] = useState(initialUser?.otp_status === true);
  const [twoFA, setTwoFA] = useState(initialUser?.google_auth_status === true);
  const [emailOtp, setEmailOtp] = useState(true);
  const [otpMethod, setOtpMethod] = useState("email");
  const [authEnrolled, setAuthEnrolled] = useState(initialUser?.google_auth_status === true);
  const [otpPolicy, setOtpPolicy] = useState("risk");
  const [otpThreshold, setOtpThreshold] = useState(45);
  const [pwdOld, setPwdOld] = useState("");
  const [pwdNew, setPwdNew] = useState("");
  const [pwdNew2, setPwdNew2] = useState("");

  // 2FA verification modal (when toggling 2FA on/off)
  const [tfaVerifyOpen, setTfaVerifyOpen] = useState(false);
  const [tfaVerifyCode, setTfaVerifyCode] = useState("");
  const [tfaVerifyPending, setTfaVerifyPending] = useState(null); // 'enable' | 'disable'
  const [tfaSetupUrl, setTfaSetupUrl] = useState(""); // QR / otpauth URL from create()
  const [securityLoading, setSecurityLoading] = useState(false);

  // Compliance state
  const [kycStatus, setKycStatus] = useState(initialUser?.kyc_status || initialUser?.kycStatus || "none");
  const [riskTier, setRiskTier] = useState(initialUser?.risk_tier || initialUser?.riskTier || "medium");
  const [pep, setPep] = useState(initialUser?.pep || false);
  const [sourceFunds, setSourceFunds] = useState(initialUser?.source_of_funds || initialUser?.sourceOfFunds || "");

  // Marketplace profile (empty until fetch-address API returns data)
  const [companyName, setCompanyName] = useState("");
  const [companyVat, setCompanyVat] = useState("");
  const [companyRegNo, setCompanyRegNo] = useState("");
  const [regAddress1, setRegAddress1] = useState("");
  const [regAddress2, setRegAddress2] = useState("");
  const [regCity, setRegCity] = useState("");
  const [regState, setRegState] = useState("");
  const [regZip, setRegZip] = useState("");
  const [regCountry, setRegCountry] = useState("");
  const [shipName, setShipName] = useState("");
  const [shipPhone, setShipPhone] = useState("");
  const [shipAddress1, setShipAddress1] = useState("");
  const [shipAddress2, setShipAddress2] = useState("");
  const [shipCity, setShipCity] = useState("");
  const [shipState, setShipState] = useState("");
  const [shipZip, setShipZip] = useState("");
  const [shipCountry, setShipCountry] = useState("");

  const [addressLoading, setAddressLoading] = useState(false);

  // Approver request
  const [approverRequested, setApproverRequested] = useState(false);
  const [approverNote, setApproverNote] = useState("");
  const [approverDocs, setApproverDocs] = useState([]);

  // Preferred currency
  const [preferredCurrency, setPreferredCurrency] = useState(initialUser?.preferred_currency || initialUser?.preferredCurrency || "USD");

  // Veriff
  const [kycVeriffStatus, setKycVeriffStatus] = useState("not_started");
  const [kybVeriffStatus, setKybVeriffStatus] = useState("not_started");
  const [veriffSessionId, setVeriffSessionId] = useState("");
  const [verificationUrl, setVerificationUrl] = useState("");
  const [veriffRedirectOpen, setVeriffRedirectOpen] = useState(false);
  const [veriffMode, setVeriffMode] = useState("kyc");

  // Sessions
  const [sessions, setSessions] = useState([
    { id: "s1", device: "Windows • Edge", ip: "93.45.12.10", country: "Italy", city: "Bari", lastSeen: "Now", risk: 58, status: "active" },
    { id: "s2", device: "iPhone • Safari", ip: "2.229.18.44", country: "Italy", city: "Rome", lastSeen: "2h ago", risk: 32, status: "active" },
    { id: "s3", device: "Unknown • TOR", ip: "185.220.101.1", country: "—", city: "—", lastSeen: "1d ago", risk: 86, status: "expired" },
  ]);

  const [audit, setAudit] = useState([
    { id: uid(), ts: "2026-01-08 08:46", actor: "system", type: "Security", detail: "OTP challenge initiated", severity: "med" },
    { id: uid(), ts: "2026-01-08 08:50", actor: "user", type: "Security", detail: "2FA enabled", severity: "low" },
  ]);

  // Profile image upload (pending file = selected but not yet saved via API)
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [pendingAvatarFile, setPendingAvatarFile] = useState(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const [pendingCoverFile, setPendingCoverFile] = useState(null);

  // Toasts
  const [toasts, setToasts] = useState([]);
  const pushToast = (title, msg) => {
    const id = uid();
    setToasts((s) => [{ id, title, msg }, ...s].slice(0, 4));
    window.setTimeout(() => setToasts((s) => s.filter((x) => x.id !== id)), 3200);
  };

  const uploadProfileImage = async (file) => {
    if (!file) return;
    setAvatarUploading(true);
    try {
      const formData = new FormData();
      formData.append("File", file);
      const nickname = (account || "").replace(/^@/, "").trim();
      if (nickname) formData.append("username", nickname);
      if (phone !== undefined && phone !== "") formData.append("phone", phone);
      await uploadProfile(formData);
      const response = await getUserInfo();
      if (response?.status === 200 && response?.data?.User) {
        const user = response.data.User;
        setUserData(user);
        if (user.image) setAvatar(user.image);
        try {
          const storedUser = JSON.parse(localStorage.getItem("user"));
          if (storedUser) {
            storedUser.data.user = { ...storedUser.data.user, ...user };
            localStorage.setItem("user", JSON.stringify(storedUser));
          }
        } catch (e) {
          console.error("Error updating localStorage:", e);
        }
        pushToast("Profile", "Profile image updated");
        setAudit((s) => [{ id: uid(), ts: new Date().toISOString().slice(0, 16).replace("T", " "), actor: "user", type: "Profile", detail: "Avatar updated", severity: "low" }, ...s]);
        setRefreshUserData((prev) => !prev);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.Message || "Upload failed";
      pushToast("Profile", msg);
    } finally {
      setAvatarUploading(false);
      setPendingAvatarFile(null);
    }
  };

  const onAvatarFileSelected = (file) => {
    if (!file) return;
    setPendingAvatarFile(file);
    setAvatar(URL.createObjectURL(file));
  };

  const dataUriToFile = (dataUri) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || 256;
        canvas.height = img.naturalHeight || 256;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        canvas.toBlob(
          (blob) => resolve(blob ? new File([blob], "avatar.png", { type: "image/png" }) : null),
          "image/png"
        );
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = dataUri;
    });

  const urlToFile = async (url) => {
    const res = await fetch(url);
    const blob = await res.blob();
    const name = url.split("/").pop() || "avatar.jpg";
    const mime = blob.type || "image/jpeg";
    return new File([blob], name, { type: mime });
  };

  const onPresetAvatarSelected = async (src) => {
    if (!src) return;
    try {
      const file = src.startsWith("data:")
        ? await dataUriToFile(src)
        : await urlToFile(src);
      if (file) setPendingAvatarFile(file);
    } catch (e) {
      console.error("Preset avatar to file failed:", e);
    }
  };

  const saveProfileImage = async () => {
    if (!pendingAvatarFile) return;
    await uploadProfileImage(pendingAvatarFile);
  };

  const clearPendingAvatar = () => setPendingAvatarFile(null);

  const onCoverFileSelected = (file) => {
    if (!file) return;
    setPendingCoverFile(file);
    setCover(URL.createObjectURL(file));
  };

  const uploadCoverImage = async (file) => {
    if (!file) return;
    setCoverUploading(true);
    try {
      const formData = new FormData();
      formData.append("File2", file);
      await uploadProfile(formData);
      const res = await getUserInfo();
      if (res?.status === 200 && res?.data?.User) {
        const user = res.data.User;
        setUserData(user);
        if (user.cover || user.coverImage) setCover(user.cover || user.coverImage);
        try {
          const stored = JSON.parse(localStorage.getItem("user"));
          if (stored) {
            stored.data.user = { ...stored.data.user, ...user };
            localStorage.setItem("user", JSON.stringify(stored));
          }
        } catch (e) {
          console.error("Error updating localStorage:", e);
        }
        setRefreshUserData((prev) => !prev);
        setPendingCoverFile(null);
        pushToast("Profile", "Cover image updated");
        setAudit((s) => [{ id: uid(), ts: new Date().toISOString().slice(0, 16).replace("T", " "), actor: "user", type: "Profile", detail: "Cover updated", severity: "low" }, ...s]);
      }
    } catch (err) {
      pushToast("Profile", err?.response?.data?.message || err?.response?.data?.Message || "Cover upload failed");
    } finally {
      setCoverUploading(false);
      setPendingCoverFile(null);
    }
  };

  const saveCoverImage = async () => {
    if (!pendingCoverFile) return;
    await uploadCoverImage(pendingCoverFile);
  };

  const clearPendingCover = () => {
    setPendingCoverFile(null);
    setCover(userData?.cover || userData?.coverImage || "");
  };

  const applyAddressData = (data) => {
    if (!data || typeof data !== "object") return;
    const empty = (v) => (v != null && v !== "" ? String(v) : "");
    // API returns company, registeredAddress, shippingAddress as arrays; use first element
    const company = Array.isArray(data.company) ? data.company[0] : data.company;
    const registeredAddress = Array.isArray(data.registeredAddress) ? data.registeredAddress[0] : data.registeredAddress;
    const shippingAddress = Array.isArray(data.shippingAddress) ? data.shippingAddress[0] : data.shippingAddress;

    if (company && typeof company === "object") {
      setCompanyName(empty(company.legalName ?? company.legal_name ?? company.company_name));
      setCompanyVat(empty(company.vatOrTaxId ?? company.vat_or_tax_id ?? company.vat_number));
      setCompanyRegNo(empty(company.registrationNo ?? company.registration_no ?? company.registration_number));
    }
    if (registeredAddress && typeof registeredAddress === "object") {
      const r = registeredAddress;
      setRegAddress1(empty(r.address1 ?? r.address_1 ?? r.reg_address1));
      setRegAddress2(empty(r.address2 ?? r.address_2 ?? r.reg_address2));
      setRegCity(empty(r.city ?? r.reg_city));
      setRegState(empty(r.state ?? r.reg_state));
      setRegZip(empty(r.zip ?? r.postal_code ?? r.reg_zip));
      setRegCountry(empty(r.country ?? r.reg_country));
    }
    if (shippingAddress && typeof shippingAddress === "object") {
      const s = shippingAddress;
      setShipName(empty(s.receiverName ?? s.receiver_name ?? s.ship_name));
      setShipPhone(empty(s.phone ?? s.ship_phone));
      setShipAddress1(empty(s.address1 ?? s.address_1 ?? s.ship_address1));
      setShipAddress2(empty(s.address2 ?? s.address_2 ?? s.ship_address2));
      setShipCity(empty(s.city ?? s.ship_city));
      setShipState(empty(s.state ?? s.ship_state));
      setShipZip(empty(s.zip ?? s.ship_zip));
      setShipCountry(empty(s.country ?? s.ship_country));
    }
  };

  const saveAddressInfo = async (payload) => {
    setAddressLoading(true);
    try {
      const res = await editAddressInfo(payload);
      if (res?.status === 200) {
        pushToast("Marketplace", "Address info saved");
        const fresh = await getAddressInfo();
        const payload = fresh?.data?.data ?? fresh?.data;
        if (payload && typeof payload === "object") applyAddressData(payload);
        setAudit((s) => [{ id: uid(), ts: new Date().toISOString().slice(0, 16).replace("T", " "), actor: "user", type: "Marketplace", detail: "Address profile updated", severity: "low" }, ...s]);
        return true;
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.Message || "Save failed";
      pushToast("Marketplace", msg);
      return false;
    } finally {
      setAddressLoading(false);
    }
    return false;
  };

  const saveProfile = async (payload = {}) => {
    const { fullName: name, account: acc, email: em, phone: ph } = payload;
    const nickname = (acc ?? account).replace(/^@/, "").trim();
    setAvatarUploading(true);
    try {
      const formData = new FormData();
      if (pendingAvatarFile) formData.append("File", pendingAvatarFile);
      if (nickname) formData.append("username", nickname);
      if (ph !== undefined) formData.append("phone", ph ?? phone);
      if (name !== undefined) formData.append("fullName", name ?? fullName);
      if (em !== undefined) formData.append("email", em ?? email);
      await uploadProfile(formData);
      const response = await getUserInfo();
      if (response?.status === 200 && response?.data?.User) {
        const user = response.data.User;
        setUserData(user);
        if (user.image) setAvatar(user.image);
        if (user.firstName || user.lastName) setFullName([user.firstName, user.lastName].filter(Boolean).join(" ").trim() || fullName);
        if (user.email) setEmail(user.email);
        if (user.phone !== undefined || user.phoneNumber !== undefined) setPhone(user.phone || user.phoneNumber || "");
        if (user.username) setAccount(`@${user.username}`);
        else if (user.email) setAccount(`@${user.email.split("@")[0]}`);
        try {
          const storedUser = JSON.parse(localStorage.getItem("user"));
          if (storedUser) {
            storedUser.data.user = { ...storedUser.data.user, ...user };
            localStorage.setItem("user", JSON.stringify(storedUser));
          }
        } catch (e) {
          console.error("Error updating localStorage:", e);
        }
        setRefreshUserData((prev) => !prev);
        setPendingAvatarFile(null);
        pushToast("Profile", "Profile saved");
        setAudit((s) => [{ id: uid(), ts: new Date().toISOString().slice(0, 16).replace("T", " "), actor: "user", type: "Profile", detail: "Profile updated", severity: "low" }, ...s]);
        return true;
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.Message || "Save failed";
      pushToast("Profile", msg);
      return false;
    } finally {
      setAvatarUploading(false);
    }
    return false;
  };

  const handle2FAChange = async (v) => {
    setTfaVerifyCode("");
    if (v) {
      setSecurityLoading(true);
      try {
        const res = await create();
        if (res?.data?.tfaURL) {
          setTfaSetupUrl(res.data.tfaURL);
          setTfaVerifyPending("enable");
          setTfaVerifyOpen(true);
        } else {
          pushToast("Security", "Failed to get 2FA setup");
        }
      } catch (err) {
        pushToast("Security", err?.response?.data?.Message || "Failed to load 2FA setup");
      } finally {
        setSecurityLoading(false);
      }
    } else {
      setTfaSetupUrl("");
      setTfaVerifyPending("disable");
      setTfaVerifyOpen(true);
    }
  };

  const handleTfaVerifySubmit = async () => {
    const code = tfaVerifyCode.replace(/[^0-9]/g, "").trim();
    if (code.length < 6) {
      pushToast("Security", "Enter the 6-digit code from your authenticator app");
      return;
    }
    setSecurityLoading(true);
    try {
      const res = await verifyTFA({ token: code });
      if (res?.data?.verified === true) {
        if (tfaVerifyPending === "enable") {
          const enableRes = await tfaEnable();
          if (enableRes?.data) {
            setTwoFA(true);
            setAuthEnrolled(true);
            setTfaSetupUrl("");
            pushToast("Security", enableRes.data.Message || "2FA enabled");
            setAudit((s) => [{ id: uid(), ts: new Date().toISOString().slice(0, 16).replace("T", " "), actor: "user", type: "Security", detail: "2FA enabled", severity: "low" }, ...s]);
            setTfaVerifyOpen(false);
            setTfaVerifyCode("");
            setTfaVerifyPending(null);
            setTfaSetupUrl("");
            try {
              const refetch = await getUserInfo();
              if (refetch?.status === 200 && refetch?.data?.User) {
                const user = refetch.data.User;
                setUserData(user);
                if (user.google_auth_status !== undefined) {
                  setTwoFA(user.google_auth_status === true);
                  setAuthEnrolled(user.google_auth_status === true);
                }
                try {
                  const stored = JSON.parse(localStorage.getItem("user"));
                  if (stored) {
                    stored.data.user = { ...stored.data.user, ...user };
                    localStorage.setItem("user", JSON.stringify(stored));
                  }
                } catch (e) {
                  console.error("Error updating localStorage:", e);
                }
                setRefreshUserData((prev) => !prev);
              }
            } catch (e) {
              console.error("Error refetching user after 2FA enable:", e);
            }
          } else {
            pushToast("Security", enableRes?.data?.Message || "2FA enable failed");
          }
        } else {
          const disableRes = await tfaDisable({ token: code });
          if (disableRes?.data) {
            setTwoFA(false);
            pushToast("Security", disableRes.data.Message || "2FA disabled");
            setAudit((s) => [{ id: uid(), ts: new Date().toISOString().slice(0, 16).replace("T", " "), actor: "user", type: "Security", detail: "2FA disabled", severity: "med" }, ...s]);
            setTfaVerifyOpen(false);
            setTfaVerifyCode("");
            setTfaVerifyPending(null);
            setTfaSetupUrl("");
            try {
              const refetch = await getUserInfo();
              if (refetch?.status === 200 && refetch?.data?.User) {
                const user = refetch.data.User;
                setUserData(user);
                if (user.google_auth_status !== undefined) {
                  setTwoFA(user.google_auth_status === true);
                  setAuthEnrolled(user.google_auth_status === true);
                }
                try {
                  const stored = JSON.parse(localStorage.getItem("user"));
                  if (stored) {
                    stored.data.user = { ...stored.data.user, ...user };
                    localStorage.setItem("user", JSON.stringify(stored));
                  }
                } catch (e) {
                  console.error("Error updating localStorage:", e);
                }
                setRefreshUserData((prev) => !prev);
              }
            } catch (e) {
              console.error("Error refetching user after 2FA disable:", e);
            }
          } else {
            pushToast("Security", disableRes?.data?.Message || "2FA disable failed");
          }
        }
      } else {
        pushToast("Security", res?.data?.Message || "Invalid code");
      }
    } catch (err) {
      pushToast("Security", err?.response?.data?.Message || "Verification failed");
    } finally {
      setSecurityLoading(false);

    }
  };

  const handleSendTwoFactor = async (v) => {
    setStatusTwoFA(v);
    setSecurityLoading(true);
    try {
      const response = await SendTwoFactor();
      const newStatus = response?.data?.data?.otp_status;
      if (newStatus !== undefined) {
        setStatusTwoFA(newStatus === true);
      }
      pushToast("Security", newStatus === true ? "Two Factor enabled" : "Two Factor disabled");
      setAudit((s) => [
        { id: uid(), ts: new Date().toISOString().slice(0, 16).replace("T", " "), actor: "user", type: "Security", detail: newStatus === true ? "Two Factor (login OTP) enabled" : "Two Factor (login OTP) disabled", severity: "low" },
        ...s,
      ]);
      const res = await getUserInfo();
      if (res?.status === 200 && res?.data?.User) {
        const user = res.data.User;
        setUserData(user);
        try {
          const storedUser = JSON.parse(localStorage.getItem("user"));
          if (storedUser) {
            storedUser.data.user = { ...storedUser.data.user, ...user };
            localStorage.setItem("user", JSON.stringify(storedUser));
          }
        } catch (e) {
          console.error("Error updating localStorage:", e);
        }
      }
    } catch (err) {
      setStatusTwoFA(!v);
      pushToast("Security", err?.response?.data?.Message || err?.message || "Failed to change Two Factor");
    } finally {
      setSecurityLoading(false);
    }
  };

  const handleRequestPasswordOtp = async () => {
    try {
      const res = await sendOtpDynamic({ type: "change-password" });
      if (res?.status === 200 || res?.status === 201) {
        setPwdOtpSentAt(Date.now());
        setPwdOtpOpen(true);
        pushToast("Security", "OTP sent to your email");
      } else {
        pushToast("Security", res?.data?.message || "Failed to send OTP");
      }
    } catch (err) {
      pushToast("Security", err?.response?.data?.message || "Failed to send OTP");
    }
  };

  const handlePasswordUpdateNoOtp = async () => {
    if (!pwdOld || !pwdNew) {
      pushToast("Password", "Enter current and new password");
      return;
    }
    if (pwdNew !== pwdNew2) {
      pushToast("Password", "New passwords do not match");
      return;
    }
    setSecurityLoading(true);
    try {
      const res = await changePassword({ password: pwdOld, newPassword: pwdNew });
      if (res?.status === 200) {
        pushToast("Password", "Password updated successfully");
        setPwdOld("");
        setPwdNew("");
        setPwdNew2("");
        setAudit((s) => [{ id: uid(), ts: new Date().toISOString().slice(0, 16).replace("T", " "), actor: "user", type: "Security", detail: "Password updated", severity: "low" }, ...s]);
      } else {
        pushToast("Password", res?.data?.message || "Update failed");
      }
    } catch (err) {
      pushToast("Password", err?.response?.data?.message || "Update failed");
    } finally {
      setSecurityLoading(false);
    }
  };

  const handlePasswordConfirm = async () => {
    const code = pwdOtp.replace(/[^0-9]/g, "").trim();
    if (!code || code.length < 6) {
      pushToast("Password", "Enter the 6-digit OTP");
      return;
    }
    if (!pwdOld || !pwdNew) {
      pushToast("Password", "Enter current and new password");
      return;
    }
    if (pwdNew !== pwdNew2) {
      pushToast("Password", "New passwords do not match");
      return;
    }
    setSecurityLoading(true);
    try {
      const res = await changePassword({ password: pwdOld, newPassword: pwdNew, otp: code });
      if (res?.status === 200) {
        pushToast("Password", "Password updated successfully");
        setPwdOtpOpen(false);
        setPwdOld("");
        setPwdNew("");
        setPwdNew2("");
        setPwdOtp("");
        setAudit((s) => [{ id: uid(), ts: new Date().toISOString().slice(0, 16).replace("T", " "), actor: "user", type: "Security", detail: "Password updated", severity: "low" }, ...s]);
      } else {
        pushToast("Password", res?.data?.message || "Update failed");
      }
    } catch (err) {
      pushToast("Password", err?.response?.data?.message || "Update failed");
    } finally {
      setSecurityLoading(false);
    }
  };

  // Modals
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpSentAt, setOtpSentAt] = useState(Date.now());
  const [pwdOtpOpen, setPwdOtpOpen] = useState(false);
  const [pwdOtp, setPwdOtp] = useState("");
  const [pwdOtpSentAt, setPwdOtpSentAt] = useState(0);
  const [authEnrollOpen, setAuthEnrollOpen] = useState(false);
  const [codesOpen, setCodesOpen] = useState(false);
  const [codes, setCodes] = useState(makeBackupCodes());
  const [codesRevealed, setCodesRevealed] = useState(false);

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 600);
    return () => window.clearInterval(t);
  }, []);

  // Fetch user data from API
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const response = await getUserInfo();
        
        if (response?.status === 200 && response?.data?.User) {
          const user = response.data.User;
          setUserData(user);
          
          // Update profile data
          const userName = user.firstName && user.lastName 
            ? `${user.firstName} ${user.lastName}` 
            : user.firstName || user.lastName || "";
          if (userName) setFullName(userName);
          
          if (user.email) setEmail(user.email);
          if (user.phone || user.phoneNumber) setPhone(user.phone || user.phoneNumber);
          if (user.image) setAvatar(user.image);
          if (user.cover || user.coverImage) setCover(user.cover || user.coverImage);
          if (user.username) setAccount(`@${user.username}`);
          else if (user.email) setAccount(`@${user.email.split("@")[0]}`);
          
          // Update wallet addresses (support API field names: address, secondary_wallet_sol_address, secondary_wallet_address_tron)
          setWallets({
            sol: user.sol_address || user.secondary_wallet_sol_address || "",
            btc: user.btcAddress || "",
            trc: user.trc_address || user.secondary_wallet_address_tron || "",
            eth: user.eth_address ||  "",
            bnb:  user.address || "",
          });
          
          // Update security settings: google_auth_status = Authenticator; otp_status = Two Factor (login OTP)
          if (user.google_auth_status !== undefined) {
            setTwoFA(user.google_auth_status === true);
            setAuthEnrolled(user.google_auth_status === true);
          }
          if (user.otp_status !== undefined) {
            setStatusTwoFA(user.otp_status === true);
          }
          
          // Update compliance data
          if (user.kyc_status || user.kycStatus) {
            setKycStatus(user.kyc_status || user.kycStatus);
          }
          if (user.risk_tier || user.riskTier) {
            setRiskTier(user.risk_tier || user.riskTier);
          }
          if (user.pep !== undefined) {
            setPep(user.pep);
          }
          if (user.source_of_funds || user.sourceOfFunds) {
            setSourceFunds(user.source_of_funds || user.sourceOfFunds);
          }
          
          // Update marketplace profile if available
          if (user.company_name || user.companyName) {
            setCompanyName(user.company_name || user.companyName);
          }
          if (user.vat_number || user.vatNumber) {
            setCompanyVat(user.vat_number || user.vatNumber);
          }
          if (user.registration_number || user.registrationNumber) {
            setCompanyRegNo(user.registration_number || user.registrationNumber);
          }
          
          // Update addresses if available
          if (user.complete_address || user.completeAddress) {
            const addressParts = (user.complete_address || user.completeAddress).split(",");
            if (addressParts.length > 0) setRegAddress1(addressParts[0].trim());
            if (addressParts.length > 1) setRegAddress2(addressParts.slice(1).join(",").trim());
          }
          if (user.city) setRegCity(user.city);
          if (user.state || user.province) setRegState(user.state || user.province);
          if (user.zip || user.postal_code || user.postalCode) setRegZip(user.zip || user.postal_code || user.postalCode);
          if (user.country) setRegCountry(user.country);
          
          // Update localStorage
          try {
            const storedUser = JSON.parse(localStorage.getItem("user"));
            if (storedUser) {
              storedUser.data.user = { ...storedUser.data.user, ...user };
              localStorage.setItem("user", JSON.stringify(storedUser));
            }
          } catch (e) {
            console.error("Error updating localStorage:", e);
          }
        }

        // Fetch address from API (fetch-address); show empty if no data
        try {
          const addrRes = await getAddressInfo();
          const payload = addrRes?.data?.data ?? addrRes?.data;
          if (payload && typeof payload === "object") {
            applyAddressData(payload);
          }
        } catch (_) {
          // No address data: fields stay empty
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        // Fallback to localStorage data
        const storedUser = getInitialUserData();
        if (storedUser) {
          setUserData(storedUser);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  // Security score: 100 if OTP + 2FA both on; -15 only OTP or only 2FA (85); -30 neither (70)
  const overallRisk = useMemo(() => {
    const hasOtp = statusTwoFA === true; // Two Factor (login OTP)
    const has2fa = twoFA === true;        // 2FA Authenticator
    if (hasOtp && has2fa) return 100;
    if (hasOtp || has2fa) return 85;   // only one: 100 - 15
    return 70;                         // neither: 100 - 30
  }, [twoFA, statusTwoFA]);

  const otpWait = useMemo(() => Math.max(0, 3 * 60 * 1000 - (now - otpSentAt)), [now, otpSentAt]);
  const pwdOtpCooldownSec = useMemo(() => (overallRisk >= 45 ? 180 : 60), [overallRisk]);
  const pwdOtpWait = useMemo(() => {
    if (!pwdOtpSentAt) return 0;
    const left = pwdOtpCooldownSec * 1000 - (now - pwdOtpSentAt);
    return Math.max(0, left);
  }, [now, pwdOtpSentAt, pwdOtpCooldownSec]);

  const needPasswordOtp = useMemo(() => (otpPolicy === "always" ? true : overallRisk >= otpThreshold), [otpPolicy, overallRisk, otpThreshold]);

  const normalizeAccount = (raw) => {
    const clean = raw.replace(/^@/, "").replace(/[^a-zA-Z0-9_]/g, "").slice(0, 20);
    return "@" + clean;
  };

  const checkAccountUnique = async (acc) => {
    const a = normalizeAccount(acc);
    if (a.length < 3) {
      setAccountCheck("idle");
      return;
    }
    setAccountCheck("checking");
    await new Promise((r) => setTimeout(r, 550));
    const taken = ["@admin", "@support", "@custodiy", "@root"].includes(a.toLowerCase()) || a.toLowerCase().endsWith("_bot");
    setAccountCheck(taken ? "taken" : "available");
    setAccountLastChecked(a);
  };

  const tabs = [
    { k: "profile", label: "Profile", icon: "user" },
    { k: "addresses", label: "Marketplace Profile", icon: "map" },
    { k: "kyb", label: "KYB (Company)", icon: "shield" },
    { k: "kyc", label: "KYC (Person)", icon: "shield" },
    { k: "security", label: "Security", icon: "lock" },
    { k: "sessions", label: "Sessions", icon: "map" },
    { k: "audit", label: "Audit Log", icon: "log" },
    { k: "compliance", label: "Compliance", icon: "shield" },
    { k: "other", label: "Other Settings", icon: "shield" },
  ];

  const commonProps = {
    fullName,
    setFullName,
    initials,
    account,
    setAccount,
    accountLocked,
    setAccountLocked,
    accountCheck,
    setAccountCheck,
    accountLastChecked,
    setAccountLastChecked,
    email,
    setEmail,
    phone,
    setPhone,
    avatar,
    setAvatar,
    cover,
    setCover,
    wallets,
    setWallets,
    userData,
    loading,
    statusTwoFA,
    setStatusTwoFA,
    handleSendTwoFactor,
    twoFA,
    setTwoFA,
    emailOtp,
    setEmailOtp,
    otpMethod,
    setOtpMethod,
    authEnrolled,
    setAuthEnrolled,
    otpPolicy,
    setOtpPolicy,
    otpThreshold,
    setOtpThreshold,
    pwdOld,
    setPwdOld,
    pwdNew,
    setPwdNew,
    pwdNew2,
    setPwdNew2,
    kycStatus,
    setKycStatus,
    riskTier,
    setRiskTier,
    pep,
    setPep,
    sourceFunds,
    setSourceFunds,
    companyName,
    setCompanyName,
    companyVat,
    setCompanyVat,
    companyRegNo,
    setCompanyRegNo,
    regAddress1,
    setRegAddress1,
    regAddress2,
    setRegAddress2,
    regCity,
    setRegCity,
    regState,
    setRegState,
    regZip,
    setRegZip,
    regCountry,
    setRegCountry,
    shipName,
    setShipName,
    shipPhone,
    setShipPhone,
    shipAddress1,
    setShipAddress1,
    shipAddress2,
    setShipAddress2,
    shipCity,
    setShipCity,
    shipState,
    setShipState,
    shipZip,
    setShipZip,
    shipCountry,
    setShipCountry,
    approverRequested,
    setApproverRequested,
    approverNote,
    setApproverNote,
    approverDocs,
    setApproverDocs,
    preferredCurrency,
    setPreferredCurrency,
    kycVeriffStatus,
    setKycVeriffStatus,
    kybVeriffStatus,
    setKybVeriffStatus,
    veriffSessionId,
    setVeriffSessionId,
    verificationUrl,
    setVerificationUrl,
    veriffRedirectOpen,
    setVeriffRedirectOpen,
    veriffMode,
    setVeriffMode,
    sessions,
    setSessions,
    audit,
    setAudit,
    pushToast,
    uid,
    overallRisk,
    otpWait,
    pwdOtpWait,
    pwdOtpCooldownSec,
    needPasswordOtp,
    normalizeAccount,
    checkAccountUnique,
    riskLabel,
    uploadProfileImage,
    avatarUploading,
    pendingAvatarFile,
    onAvatarFileSelected,
    onPresetAvatarSelected,
    saveProfileImage,
    clearPendingAvatar,
    coverUploading,
    pendingCoverFile,
    onCoverFileSelected,
    uploadCoverImage,
    saveCoverImage,
    clearPendingCover,
    saveProfile,
    tfaVerifyOpen,
    setTfaVerifyOpen,
    tfaVerifyCode,
    setTfaVerifyCode,
    tfaVerifyPending,
    setTfaVerifyPending,
    tfaSetupUrl,
    setTfaSetupUrl,
    handle2FAChange,
    handleTfaVerifySubmit,
    securityLoading,
    handleRequestPasswordOtp,
    handlePasswordConfirm,
    handlePasswordUpdateNoOtp,
    addressLoading,
    saveAddressInfo,
  };

  if (loading) {
    return (
      <PageContainer>
        <MaxWidthContainer>
          <div style={{ display: "grid", placeItems: "center", minHeight: "100vh", color: "rgba(240,250,255,.92)" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 900, marginBottom: 12 }}>Loading...</div>
              <div style={{ fontSize: 12, color: "rgba(210,235,245,.75)" }}>Fetching your account data</div>
            </div>
          </div>
        </MaxWidthContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <MaxWidthContainer>
      
        <MainGrid>
          <SideNavContainer>
            <SecurityConsole>
              <SecurityConsoleHeader>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 12, border: "1px solid rgba(92,233,255,.16)", background: "radial-gradient(circle at 30% 20%, rgba(92,233,255,.22), rgba(0,0,0,0) 60%), rgba(0,0,0,.22)", display: "grid", placeItems: "center" }}>
                    <Icon name="shield" />
                  </div>
                  <div style={{ display: "grid", gap: 2 }}>
                    <div style={{ fontWeight: 900 }}>Security Console</div>
                    <div style={{ fontSize: 12, color: "rgba(210,235,245,.75)" }}>Realtime posture: 2FA • OTP • Sessions • Audit</div>
                  </div>
                </div>
                <ProgressRing value={overallRisk} />
              </SecurityConsoleHeader>
              <SecurityConsoleContent>
                {tabs.map((t) => {
                  const active = tab === t.k;
                  return (
                    <NavButton key={t.k} onClick={() => setTab(t.k)} active={active}>
                      <NavButtonLeft>
                        <NavButtonIcon>
                          <Icon name={t.icon} />
                        </NavButtonIcon>
                        <NavButtonLabel>{t.label}</NavButtonLabel>
                      </NavButtonLeft>
                      <NavButtonRight active={active}>
                        {active ? <Pill>Active</Pill> : <span style={{ opacity: 0.55 }}>Open</span>}
                      </NavButtonRight>
                    </NavButton>
                  );
                })}
              </SecurityConsoleContent>
            </SecurityConsole>

            <Card title="Quick Actions" icon="key">
              <ContentGrid>
                <Button
                  onClick={() => {
                    setTab("security");
                    pushToast("Quick", "Jumped to Security");
                  }}
                  variant="sec"
                >
                  Open Security
                </Button>
              </ContentGrid>
            </Card>
          </SideNavContainer>

          <MainContent>
            {tab === "profile" && <ProfileView {...commonProps} />}
            {tab === "addresses" && <AddressesView {...commonProps} />}
            {tab === "kyb" && <KYBView {...commonProps} />}
            {tab === "kyc" && <KYCView {...commonProps} />}
            {tab === "security" && <SecurityView {...commonProps} otpOpen={otpOpen} setOtpOpen={setOtpOpen} otpCode={otpCode} setOtpCode={setOtpCode} otpSentAt={otpSentAt} setOtpSentAt={setOtpSentAt} pwdOtpOpen={pwdOtpOpen} setPwdOtpOpen={setPwdOtpOpen} pwdOtp={pwdOtp} setPwdOtp={setPwdOtp} pwdOtpSentAt={pwdOtpSentAt} setPwdOtpSentAt={setPwdOtpSentAt} authEnrollOpen={authEnrollOpen} setAuthEnrollOpen={setAuthEnrollOpen} codesOpen={codesOpen} setCodesOpen={setCodesOpen} codes={codes} setCodes={setCodes} codesRevealed={codesRevealed} setCodesRevealed={setCodesRevealed} makeBackupCodes={makeBackupCodes} />}
            {tab === "sessions" && <SessionsView {...commonProps} />}
            {tab === "audit" && <AuditView {...commonProps} />}
            {tab === "compliance" && <ComplianceView {...commonProps} />}
            {tab === "other" && <OtherView {...commonProps} />}
          </MainContent>
        </MainGrid>

        <FooterNote>
          Demo note: Mapbox + real APIs can be wired later (sessions, audit export, KYC provider, 2FA secrets).
        </FooterNote>
      </MaxWidthContainer>

      <ToastStack items={toasts} onDismiss={(id) => setToasts((s) => s.filter((x) => x.id !== id))} />
    </PageContainer>
  );
};

export default AccountManagement;
